import { styled } from '@linaria/react';
import { bodyFont, colors, fonts, layout, semantic, space } from '../../theme/tokens';
import { Badge } from '../Badge';

export const ItemCardBase = styled.div<{ $unavailable?: boolean; $clickable: boolean }>`
  display: flex;
  flex-direction: column;
  background: ${colors.white};
  cursor: ${({ $clickable }) => ($clickable ? 'pointer' : 'default')};
  opacity: ${({ $unavailable }) => ($unavailable ? 0.55 : 1)};
  transition: transform 0.2s ease;

  &:focus-visible {
    outline: 2px solid ${colors.primary};
    outline-offset: 2px;
    border-radius: ${layout.borderRadiusXl};
  }
`;

export const ImageWrapper = styled.div<{
  $aspectRatio?: string;
  $mobileAspectRatio?: string;
  $imageOverlay?: boolean;
}>`
  position: relative;
  width: 100%;
  aspect-ratio: ${({ $mobileAspectRatio, $aspectRatio }) =>
    $mobileAspectRatio ?? $aspectRatio ?? '4 / 3'};
  overflow: hidden;
  border-radius: ${layout.borderRadiusXl};
  background: ${semantic.bg.surfaceSubtle};
  isolation: isolate;

  @media (min-width: ${layout.desktopBreakpoint}) {
    aspect-ratio: ${({ $aspectRatio }) => $aspectRatio ?? '4 / 3'};
  }

  &::after {
    content: ${({ $imageOverlay }) => ($imageOverlay ? "''" : 'none')};
    position: absolute;
    inset: 0;
    background: rgba(0, 0, 0, 0.05);
    mix-blend-mode: multiply;
    pointer-events: none;
  }
`;

export const Image = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

export const Placeholder = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${semantic.text.tertiary};
  font-family: ${bodyFont};
  font-size: ${fonts.size.xs};
`;

export const ImageBadge = styled(Badge)`
  position: absolute;
  top: ${space[3]};
  left: ${space[3]};
`;

export const ImageAction = styled.button`
  position: absolute;
  z-index: 1;
  top: ${space[3]};
  right: ${space[3]};
  display: inline-flex;
  align-items: center;
  gap: ${space[1]};
  font-family: ${fonts.body};
  font-size: ${fonts.size.xs};
  font-weight: ${fonts.weight.medium};
  color: ${colors.white};
  background: rgba(13, 13, 13, 0.45);
  border: none;
  border-radius: ${layout.borderRadiusFull};
  padding: ${space[1]} ${space[3]};
  cursor: pointer;
  transition: background 0.2s ease;

  &:hover {
    background: rgba(13, 13, 13, 0.65);
  }

  &:focus-visible {
    outline: 2px solid ${colors.white};
    outline-offset: 2px;
  }
`;

export const Content = styled.div<{ $textPosition: 'left' | 'center' | 'right' }>`
  display: flex;
  flex-direction: column;
  gap: ${space[4]};
  padding: ${space['20px']} 0 ${space[2]};
  text-align: ${({ $textPosition }) => $textPosition};
`;

export const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${space[3]};
`;

export const Title = styled.h3<{ $bold?: boolean }>`
  font-family: ${bodyFont};
  font-size: ${fonts.size.base};
  font-weight: ${({ $bold }) => ($bold ? fonts.weight.semiBold : fonts.weight.normal)};
  color: ${semantic.text.primary};
  margin: 0;
  line-height: 1.3;
  flex: 1;
  min-width: 0;
`;

export const ActionButton = styled.button`
  font-family: ${fonts.body};
  font-size: ${fonts.size.xs};
  font-weight: ${fonts.weight.semiBold};
  background: ${semantic.text.primary};
  color: ${colors.white};
  border: none;
  border-radius: 20px;
  padding: 8px 20px;
  cursor: pointer;
  white-space: nowrap;
  transition: opacity 0.2s ease;
  flex-shrink: 0;

  &:hover {
    opacity: 0.85;
  }
`;

export const Meta = styled.div`
  display: flex;
  align-items: center;
  gap: ${space[3]};
  font-family: ${bodyFont};
  font-size: ${fonts.size.xs};
  color: ${semantic.text.secondary};
`;

export const PriceRow = styled.div`
  font-family: ${bodyFont};
  font-size: ${fonts.size.md};
  color: ${semantic.text.primary};
`;

export const StatusBadge = styled(Badge)`
  align-self: flex-start;
`;

export const UnavailableOverlay = styled.div`
  position: absolute;
  z-index: 1;
  left: 50%;
  bottom: ${space[4]};
  transform: translateX(-50%);
  background: ${colors.white};
  padding: ${space[2]} ${space[3]};
  border-radius: ${layout.borderRadiusXl};
  display: inline-flex;
  align-items: center;
  gap: ${space[3]};
  max-width: calc(100% - ${space[4]});
  white-space: nowrap;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
`;

export const UnavailableOverlayText = styled.span`
  font-family: ${fonts.body};
  font-size: ${fonts.size.xs};
  color: ${semantic.text.secondary};
`;

export const UnavailableOverlayDivider = styled.span`
  width: 1px;
  height: ${fonts.size.sm};
  background: ${semantic.bg.surfaceSubtle};
`;

export const OverlayContent = styled.div`
  position: absolute;
  z-index: 1;
  bottom: 0;
  left: 0;
  width: fit-content;
  padding: ${space[3]} ${space[4]};
  display: flex;
  flex-direction: column;
  gap: ${space[1]};
  background: rgba(0, 0, 0, 0.15);
  border-radius: 0 ${layout.borderRadius} 0 ${layout.borderRadius};
  color: ${colors.white};

  & * {
    color: inherit;
  }
`;

export const OverlayTitle = styled.h3`
  font-family: ${bodyFont};
  font-size: ${fonts.size.base};
  font-weight: ${fonts.weight.semiBold};
  margin: 0;
  line-height: 1.3;
`;

export const OverlayPrice = styled.div`
  font-family: ${bodyFont};
  font-size: ${fonts.size.sm};
`;
